import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Trash2, PlayCircle, Share2, Check } from 'lucide-react';
import { fmtRelative, domainOf, youtubeThumb, extractUrls, normalizeUrl, shareNote, tintColor } from '../lib/utils';
import { sourceMeta } from '../lib/sourceMeta';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';

export default function NoteCard({ doc, categories, projects, onOpen, onDelete, highlight, selected, selectMode, onToggleSelect }) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const { Icon, color, labelKey } = sourceMeta(doc.source_type);
  const label = t(labelKey);
  const isProcessing = doc.status === 'processing';
  const isFailed = doc.status === 'failed';
  const [justCopied, setJustCopied] = useState(false);
  const cat = (categories || []).find((c) => c.id === doc.category_id) || null;
  const proj = (projects || []).find((p) => p.id === doc.project_id) || null;

  // Opening on click is deferred briefly so a following double-click can
  // cancel it and hand off to the grid's drag-to-select gesture instead of
  // the modal popping open before the drag even starts.
  const openTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(openTimerRef.current), []);

  const handleCardClick = (e) => {
    if (selectMode) {
      onToggleSelect?.(doc.id);
      return;
    }
    if (e.detail >= 2) {
      clearTimeout(openTimerRef.current);
      return;
    }
    openTimerRef.current = setTimeout(() => onOpen(doc), 250);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const result = await shareNote(doc, t);
    if (result === 'copied') {
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1500);
    }
  };
  // Source-type-specific display logic (matches mobile NoteCard):
  //   • text    → user's raw input (body_text), like a sticky note
  //   • url     → title + user's reason + URL inline
  //   • youtube → thumbnail + title + user's reason + URL
  //   • pdf     → filename + user's reason
  // For non-text we show `reason` (user-editable), not Gemini summary.
  const isText = doc.source_type === 'text';
  const bodyText = isText
    ? doc.body_text || doc.summary || ''
    : doc.reason || '';
  const noteLinks = isText ? extractUrls(bodyText) : [];

  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlightText = (text) => {
    if (!highlight || !text) return text;
    const terms = highlight
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 1);
    if (terms.length === 0) return text;
    const re = new RegExp(`(${terms.map(escapeRe).join('|')})`, 'gi');
    const parts = text.split(re);
    return parts.map((p, i) =>
      re.test(p) ? (
        <mark key={i} className="bg-keep-accentBgSoft text-keep-text rounded px-0.5">
          {p}
        </mark>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      ),
    );
  };

  if (isProcessing) {
    return (
      <article
        className="bg-keep-surface border border-keep-border rounded-lg relative overflow-hidden"
        data-testid="note-card-processing"
        aria-busy="true"
      >
        <div className="p-3.5">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wide">
            <Icon className={`w-3.5 h-3.5 ${color}`} />
            <span className="text-keep-textMuted">{label}</span>
            <span className="ml-auto inline-flex items-center gap-1 text-keep-accentDark normal-case tracking-normal text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-keep-accent animate-pulse" />
              {t('noteCard.processing')}
            </span>
          </div>
          {doc.title && (
            <h3 className="text-[15px] font-medium leading-snug text-keep-text mb-2 line-clamp-2">
              {doc.title}
            </h3>
          )}
          <div className="space-y-1.5 mt-1">
            <div className="h-2.5 rounded bg-keep-hover animate-pulse" />
            <div className="h-2.5 rounded bg-keep-hover animate-pulse w-11/12" />
            <div className="h-2.5 rounded bg-keep-hover animate-pulse w-8/12" />
          </div>
          <div className="text-[11px] text-keep-textMuted mt-3 italic">{t('noteCard.processingHint')}</div>
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-keep-surface/40 to-transparent animate-[shimmer_1.6s_infinite]" />
      </article>
    );
  }

  if (isFailed) {
    return (
      <article
        className="bg-keep-surface border border-keep-dangerBorder rounded-lg p-3.5 relative"
        data-testid="note-card-failed"
      >
        <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wide">
          <Icon className={`w-3.5 h-3.5 ${color}`} />
          <span className="text-keep-danger">{t('noteCard.failed')}</span>
        </div>
        {doc.title && (
          <h3 className="text-[15px] font-medium leading-snug text-keep-text mb-1 line-clamp-2">
            {doc.title}
          </h3>
        )}
        <p className="text-[12.5px] text-keep-danger leading-snug">
          {doc.error || t('noteCard.couldNotSave')}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(doc);
          }}
          className="mt-2 text-[12px] text-keep-textMuted underline"
        >
          {t('noteCard.dismiss')}
        </button>
      </article>
    );
  }

  // ─── Display variants ────────────────────────────────────────────────────
  const ytThumb =
    doc.source_type === 'youtube'
      ? doc.preview?.image || youtubeThumb(doc.source_url)
      : '';
  const showHero =
    doc.source_type === 'youtube'
      ? !!ytThumb
      : !!doc.preview?.image && doc.source_type !== 'pdf';

  return (
    <article
      className={`group bg-keep-surface rounded-lg hover:shadow-keep transition-shadow cursor-pointer relative border-2 transition-colors duration-150 ${
        selected ? 'border-keep-text' : 'border-keep-border'
      }`}
      style={cat ? { backgroundColor: tintColor(cat.color || '#34A853', theme === 'dark') } : undefined}
      onClick={handleCardClick}
      data-testid="note-card"
      data-note-id={doc.id}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect?.(doc.id);
        }}
        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-[opacity,background-color,border-color] duration-150 ${
          selected
            ? 'bg-keep-text border-keep-text opacity-100'
            : 'bg-keep-surface/90 border-keep-textMuted opacity-0 group-hover:opacity-100'
        } ${selectMode ? '!opacity-100' : ''}`}
        aria-label={selected ? t('noteCard.deselect') : t('noteCard.select')}
        data-testid="note-card-select"
      >
        {selected && <Check className="w-3.5 h-3.5 text-keep-surface" />}
      </button>

      {/* Hero image (YouTube thumbnail or link OG image) */}
      {showHero && (
        <div className="relative">
          <img
            src={doc.source_type === 'youtube' ? ytThumb : doc.preview.image}
            alt={doc.preview?.title || doc.title || ''}
            className="w-full max-h-[180px] object-cover rounded-t-lg border-b border-keep-border"
            loading="lazy"
          />
          {['youtube', 'tiktok', 'instagram'].includes(doc.source_type) && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <PlayCircle className="w-12 h-12 text-white/90 drop-shadow-lg" strokeWidth={1.5} />
            </div>
          )}
        </div>
      )}

      <div className="p-3.5">
        <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wide">
          <Icon className={`w-3.5 h-3.5 ${color}`} />
          <span className="text-keep-textMuted">{label}</span>
          {cat && (
            <span
              className="normal-case tracking-normal px-2 py-0.5 rounded-full text-[10.5px] font-medium"
              style={{ backgroundColor: `${cat.color || '#34A853'}22`, color: cat.color || '#137333' }}
              data-testid="note-card-category-badge"
            >
              {cat.name}
            </span>
          )}
          {proj && (
            <span
              className="normal-case tracking-normal px-2 py-0.5 rounded-full text-[10.5px] font-medium border border-current"
              style={{ color: proj.color || '#137333' }}
              data-testid="note-card-project-badge"
            >
              {proj.name}
            </span>
          )}
          {doc.created_at && (
            <span className="text-keep-textFaint ml-auto">{fmtRelative(doc.created_at, t, language)}</span>
          )}
        </div>

        {doc.title && (
          <h3
            className="text-[15px] font-medium leading-snug text-keep-text mb-1 break-words"
            data-testid="note-card-title"
          >
            {highlightText(doc.title)}
          </h3>
        )}

        {/* PDF: title IS the filename — already shown above */}

        {bodyText && (
          <p
            className={`text-[13.5px] text-keep-text/90 leading-snug whitespace-pre-wrap break-words ${
              isText ? 'line-clamp-12' : 'line-clamp-6'
            }`}
            data-testid="note-card-body"
          >
            {highlightText(bodyText)}
          </p>
        )}

        {!bodyText && !isText && (
          <p className="text-[12.5px] text-keep-textFaint italic mt-1">{t('noteCard.noReason')}</p>
        )}

        {/* Inline URL/domain for url + youtube */}
        {doc.source_url && doc.source_type !== 'pdf' && (
          <div className="mt-2 flex items-center gap-1 text-[11px] text-keep-urlBlue truncate">
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{domainOf(doc.source_url) || doc.source_url}</span>
          </div>
        )}

        {/* Source list for plain notes containing multiple links */}
        {noteLinks.length > 1 && (
          <div
            className="mt-2 pt-2 border-t border-keep-border space-y-1"
            data-testid="note-card-sources"
          >
            <div className="text-[10px] uppercase tracking-wide text-keep-textFaint">
              {t('common.sources')}
            </div>
            {noteLinks.map((u, i) => (
              <a
                key={i}
                href={normalizeUrl(u)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-[11px] text-keep-urlBlue truncate hover:underline"
              >
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{domainOf(u) || u}</span>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Hover bottom toolbar */}
      {!selectMode && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-2 right-2 flex items-center gap-1 bg-keep-surface/90 backdrop-blur-sm rounded-full p-0.5 border border-keep-border">
          <button
            onClick={handleShare}
            className="p-1.5 rounded-full hover:bg-keep-hover"
            aria-label={t('common.share')}
            title={justCopied ? t('common.linkCopied') : t('common.share')}
            data-testid="note-card-share"
          >
            {justCopied ? (
              <Check className="w-3.5 h-3.5 text-keep-accentDark" />
            ) : (
              <Share2 className="w-3.5 h-3.5 text-keep-textMuted" />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(doc);
            }}
            className="p-1.5 rounded-full hover:bg-keep-hover"
            aria-label={t('common.delete')}
            data-testid="note-card-delete"
          >
            <Trash2 className="w-3.5 h-3.5 text-keep-textMuted" />
          </button>
        </div>
      )}
    </article>
  );
}
